import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import {
  createAccessInvite,
  useAccessInvites,
  revokeAccessInvite,
  queueMail,
  useCollection,
  useDoc,
} from '../data/firestore';

// Founder-only manager for platform-access invites. Creating an invite writes
// the accessGrants doc immediately, so the invitee is admitted as soon as
// they create their account on the /welcome link. Keep ACCESS_ADMINS in sync
// with accessAdmin() in firestore.rules — the rules are the real boundary,
// this constant only hides the UI from everyone else.
const ACCESS_ADMINS = ['jitochadha@gmail.com', 'jito@nventr.ai'];

const LANDINGS = [
  { value: '/idp-pricing', label: 'IDP rate card (/idp-pricing)' },
  { value: '/pricing', label: 'Platform pricing (/pricing)' },
  { value: '/plan', label: 'Business plan (/plan)' },
  { value: '/docs', label: 'Platform docs (/docs)' },
  { value: '/orgs', label: 'Platform console (/orgs)' },
];

export default function Invites() {
  const { user } = useAuth();
  const { docs: invites } = useAccessInvites();
  const { docs: leads, error: leadsError } = useCollection('leads', { orderBy: 'createdAt' });
  const [email, setEmail] = useState('');
  const [landing, setLanding] = useState('/idp-pricing');
  const [lastLink, setLastLink] = useState('');
  const [lastEmail, setLastEmail] = useState('');
  const [busy, setBusy] = useState(false);
  // The most recently queued email — its live status renders inline.
  const [mailId, setMailId] = useState(null);
  const [mailFor, setMailFor] = useState('');
  const { doc: mailDoc } = useDoc(mailId ? `mail/${mailId}` : null);

  const sendInviteEmail = async (to, link) => {
    setMailFor(to);
    setMailId(await queueMail({
      to,
      template: 'access-invite',
      params: { link, invitedBy: user.email },
    }));
  };

  const mailStatus = () => {
    if (!mailId) return null;
    const s = mailDoc?.meta?.status;
    if (!mailDoc || s === 'queued' || s === 'running') return `Sending email to ${mailFor}…`;
    if (s === 'done') return `Email sent to ${mailFor}.`;
    return `Email to ${mailFor} failed: ${mailDoc?.meta?.message || 'unknown error'}`;
  };

  if (!user || !ACCESS_ADMINS.includes(user.email)) {
    return (
      <div className="cs-page">
        <div className="cs-empty">Invite management is founder-only.</div>
      </div>
    );
  }

  const inviteLink = (token) => `${window.location.origin}/welcome?invite=${token}`;

  return (
    <div className="cs-page cs-plan">
      <h1>Access invites</h1>
      <p className="cs-store-note">
        Invites whitelist an email instantly, email the invitee their
        activation link (from no-reply@nventr.io), and hand you the link too.
        The invitee opens it, sees their email pre-filled, sets a password,
        and lands on the page you chose. Revoking removes their access
        everywhere.
      </p>

      <form
        style={{ display: 'flex', gap: 8, maxWidth: 720, flexWrap: 'wrap', marginTop: 16 }}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!email.trim() || busy) return;
          setBusy(true);
          try {
            const to = email.trim().toLowerCase();
            const token = await createAccessInvite({ email: to, landing });
            const link = inviteLink(token);
            setLastLink(link);
            setLastEmail(to);
            setEmail('');
            await sendInviteEmail(to, link);
          } finally {
            setBusy(false);
          }
        }}
      >
        <input
          type="email"
          className="cs-input"
          style={{ flex: 1, minWidth: 240 }}
          placeholder="Invite by email…"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select className="cs-input" style={{ width: 260 }} value={landing} onChange={(e) => setLanding(e.target.value)}>
          {LANDINGS.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
        <button className="cs-btn cs-btn-primary" disabled={!email.trim() || busy} type="submit">
          Create invite
        </button>
      </form>

      {lastLink && (
        <div className="cs-card" style={{ padding: 14, marginTop: 14, maxWidth: 720 }}>
          <p style={{ margin: 0 }}>
            Invite ready for <strong>{lastEmail}</strong>:
          </p>
          <p style={{ wordBreak: 'break-all', margin: '8px 0' }}>
            <code>{lastLink}</code>
          </p>
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={() => navigator.clipboard?.writeText(lastLink)}
          >
            Copy link
          </button>
        </div>
      )}

      {mailId && (
        <p className="cs-help" style={{ marginTop: 10 }}>{mailStatus()}</p>
      )}

      <h3 style={{ marginTop: 26 }}>Invites</h3>
      <div style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
        {(invites || []).map((inv) => (
          <div key={inv.id} className="cs-card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ color: 'var(--text)' }}>{inv.email}</strong>
            <span className="cs-help" style={{ fontSize: 12 }}>
              {inv.status === 'accepted' ? 'accepted' : 'pending'} · lands on {inv.landing || '/orgs'}
            </span>
            <span style={{ flex: 1 }} />
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => navigator.clipboard?.writeText(inviteLink(inv.id))}
            >
              Copy link
            </button>
            <button
              className="cs-btn cs-btn-ghost cs-btn-sm"
              onClick={() => sendInviteEmail(inv.email, inviteLink(inv.id))}
            >
              Send email
            </button>
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              onClick={() => {
                // eslint-disable-next-line no-alert
                if (window.confirm(`Revoke access for ${inv.email}? They lose entry everywhere.`)) {
                  revokeAccessInvite(inv);
                }
              }}
            >
              Revoke
            </button>
          </div>
        ))}
        {invites && invites.length === 0 && (
          <div className="cs-empty">No invites yet.</div>
        )}
      </div>

      <h3 style={{ marginTop: 36 }}>Enterprise reviews</h3>
      <div style={{ display: 'grid', gap: 8, maxWidth: 720 }}>
        {(leads || []).map((lead) => (
          <div key={lead.id} className="cs-card" style={{ padding: 12 }}>
            <strong style={{ color: 'var(--text)' }}>{lead.company}</strong>
            <div className="cs-help" style={{ fontSize: 12 }}>
              {lead.name} · {lead.email} · {lead.interest} · {lead.seats} · {lead.timeline}
            </div>
            <div style={{ marginTop: 6 }}>{lead.brief}</div>
          </div>
        ))}
        {leadsError && (
          <p className="cs-help">Leads are saved. This list is waiting on the leads rule.</p>
        )}
        {leads && !leadsError && leads.length === 0 && (
          <div className="cs-empty">No enterprise reviews yet.</div>
        )}
      </div>
    </div>
  );
}
